import React from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Typography from '@ui/typography'
import ButtonComponent from '@ui/buttons/Button'
import PayNowButtonComponent from 'features/payment/PayNow'
import Page from '@shell/Page'
import { ButtonProps } from '@ui/buttons/Button/types'

const Section = ({
  title,
  children,
}: {
  title?: string
  children?: React.ReactNode
}) => (
  <View className="gap-16">
    {title && (
      <View className="border-b border-black/10 p-4">
        <Typography
          variant="XS"
          className="text-center uppercase text-black/25"
        >
          {title}
        </Typography>
      </View>
    )}
    <View className="gap-8 px-16 pb-16">{children}</View>
  </View>
)

const Button = (props: ButtonProps) => {
  const [loading, setLoading] = React.useState(false)
  return (
    <ButtonComponent
      {...props}
      loading={loading}
      onPress={() => setLoading((prev) => !prev)}
    />
  )
}

const PAY_NOW_PRICE_SAMPLES = [0, 9.99, 99.99, 999.99, 9999.99] as const

const PayNowButton = ({ value }: { value: number }) => {
  const { t } = useTranslation('ui')

  return (
    <PayNowButtonComponent
      price={value}
      label={t('payNow')}
      onPress={() => undefined}
    />
  )
}

export default function DevButtonsPage() {
  return (
    <Page stage="dev">
      <View className="gap-8 p-8">
        <View className="border-b border-black/10 p-4">
          <Typography
            variant="XS"
            className="text-center uppercase text-black/25"
          >
            Button With Price
          </Typography>
        </View>
        <View className="flex flex-row flex-wrap justify-center gap-8">
          {PAY_NOW_PRICE_SAMPLES.map((value) => (
            <PayNowButton key={value} value={value} />
          ))}
        </View>
      </View>
      <Section title="Buttons LG">
        <View className="flex flex-row justify-between">
          <Button
            variant="outlined"
            size="lg"
            label="Button"
            iconStart="circle"
          />
          <Button variant="outlined" size="lg" label="Button" />
          <Button
            variant="outlined"
            size="lg"
            label="Button"
            iconEnd="circle"
          />
        </View>
        <View className="flex flex-row justify-between">
          <Button
            variant="filled"
            size="lg"
            label="Button"
            iconStart="circle"
          />
          <Button variant="filled" size="lg" label="Button" />
          <Button variant="filled" size="lg" label="Button" iconEnd="circle" />
        </View>
        <View className="flex flex-row justify-between">
          <Button
            variant="filled"
            size="lg"
            label="Button"
            iconStart="circle"
            disabled
          />
          <Button variant="filled" size="lg" label="Button" disabled />
          <Button
            variant="filled"
            size="lg"
            label="Button"
            iconEnd="circle"
            disabled
          />
        </View>
        <View className="flex flex-row justify-between">
          <Button
            variant="outlined"
            size="lg"
            label="Button"
            iconStart="circle"
            disabled
          />
          <Button variant="outlined" size="lg" label="Button" disabled />
          <Button
            variant="outlined"
            size="lg"
            label="Button"
            iconEnd="circle"
            disabled
          />
        </View>
      </Section>
      <Section title="Buttons MD">
        <View className="flex flex-row justify-between">
          <Button
            variant="outlined"
            size="md"
            label="Button"
            iconStart="circle"
          />
          <Button variant="outlined" size="md" label="Button" />
          <Button
            variant="outlined"
            size="md"
            label="Button"
            iconEnd="circle"
          />
        </View>
        <View className="flex flex-row justify-between">
          <Button
            variant="filled"
            size="md"
            label="Button"
            iconStart="circle"
          />
          <Button variant="filled" size="md" label="Button" />
          <Button variant="filled" size="md" label="Button" iconEnd="circle" />
        </View>
        <View className="flex flex-row justify-between">
          <Button
            variant="filled"
            size="md"
            label="Button"
            iconStart="circle"
            disabled
          />
          <Button variant="filled" size="md" label="Button" disabled />
          <Button
            variant="filled"
            size="md"
            label="Button"
            iconEnd="circle"
            disabled
          />
        </View>

        <View className="flex flex-row justify-between">
          <Button
            variant="outlined"
            size="md"
            label="Button"
            iconStart="circle"
            disabled
          />
          <Button variant="outlined" size="md" label="Button" disabled />
          <Button
            variant="outlined"
            size="md"
            label="Button"
            iconEnd="circle"
            disabled
          />
        </View>
      </Section>
      <Section title="Buttons MD Icon">
        <View className="flex flex-row justify-between">
          <Button variant="filled" size="md" iconStart="circle" />
          <Button variant="outlined" size="md" iconStart="circle" />
          <Button variant="filled" size="md" iconStart="circle" disabled />
          <Button variant="outlined" size="md" iconStart="circle" disabled />
        </View>
      </Section>
      <Section title="Buttons Small">
        <View className="flex flex-row justify-between">
          <Button variant="filled" size="sm" label="Button" />
          <Button variant="outlined" size="sm" label="Button" />
          <Button
            variant="outlined"
            badge="required"
            size="sm"
            label="Button"
          />
          <Button variant="outlined" size="sm" label="Button" disabled />
        </View>
        <View className="flex flex-row justify-between">
          <Button variant="filled" size="sm" iconStart="circle" />
          <Button variant="outlined" size="sm" iconStart="circle" />
          <Button
            variant="outlined"
            size="sm"
            iconStart="circle"
            badge="required"
          />
          <Button variant="outlined" size="sm" iconStart="circle" disabled />
        </View>
      </Section>
      <Section title="Buttons Tiny">
        <View className="flex flex-row justify-between">
          <Button variant="filled" size="xs" label="Button" />
          <Button variant="outlined" size="xs" label="Button" />
          <Button
            variant="outlined"
            badge="required"
            size="xs"
            label="Button"
          />
          <Button variant="outlined" size="xs" label="Button" disabled />
        </View>
        <View className="flex flex-row justify-between">
          <Button variant="filled" size="xs" iconStart="circle" />
          <Button variant="outlined" size="xs" iconStart="circle" />
          <Button
            variant="outlined"
            badge="required"
            size="xs"
            label="Button"
          />
          <Button variant="outlined" size="xs" iconStart="circle" disabled />
        </View>
      </Section>
    </Page>
  )
}
